import React, { ReactNode, useEffect, useState } from "react";
import { useTheme } from "../../context/useThemeContext";
import { hexToRGBA } from "../../utility/hexToRGB";
import { useGetEditor } from "../../context/useEditorContext";
import useMainStore from "../../stores/mainStore";
import styled from "styled-components";
import useDataStore from "../../stores/dataStore";
import { LuTableOfContents } from "react-icons/lu";

const TableOfContentsBTN = styled.div<{
    $hoverBG: string;
    $hoverColor: string;
}>`
    &:hover {
        background-color: ${(props) => props.$hoverBG} !important;
        color: ${(props) => props.$hoverColor} !important;
    }
`;

function TableOfContents() {
    const { theme } = useTheme();
    const editor = useGetEditor().editor.current;
    const [headings, setHeadings] = useState<
        Array<{ text: string; level: number; pos: number }>
    >([]);
    const selectedNoteID = useMainStore((state) => state.data.selectedNoteID);
    const nodeTitle = useDataStore(
        (state) => state.data.notes[selectedNoteID]?.title
    );
    const showTableOfContents = useMainStore(
        (state) => state.data.showTableOfContents
    );

    useEffect(() => {
        const updateHeadings = () => {
            const headings_: Array<{
                pos: number;
                text: string;
                level: number;
            }> = [];
            editor?.state.doc.descendants((node, pos) => {
                if (node.type.name === "heading") {
                    headings_.push({
                        pos,
                        text: node.textContent,
                        level: node.attrs.level,
                    });
                }
            });

            setHeadings(() => headings_ || []);
        };

        // editor?.commands.updateTableOfContents();
        updateHeadings();

        editor?.on("update", updateHeadings);

        return () => {
            editor?.off("update", updateHeadings);
        };
    }, []);

    if (!headings.length || !showTableOfContents) return null;

    return (
        <TableOfContentsContainer>
            <div>
                <p
                    className="text-sm font-bold mb-1  border-b pb-1 gap-1 items-center"
                    style={{
                        borderColor: hexToRGBA(theme.headerColor, 1),
                        color: theme.fontColor[50],
                        paddingLeft: "4px",
                        textWrap: "nowrap",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                    }}
                >
                    {/* <LuTableOfContents size={18}/> */}
                    {nodeTitle}
                </p>
                <div className="text-gray-400 flex flex-col">
                    {headings.map((heading) => {
                        return (
                            <TableOfContentsBTN
                                key={heading.pos}
                                $hoverBG={hexToRGBA(theme.accentColor, 0.1)}
                                $hoverColor={theme.fontColor[50]}
                                className="cursor-pointer text-sm py-[2px] px-[6px]  rounded max-w-[300px] text-nowrap text-ellipsis overflow-hidden"
                                style={{
                                    // Limit to 4 levels
                                    // for each level indent by 15px
                                    // -4 is to move it to the left
                                    paddingLeft:
                                        Math.max(
                                            6,
                                            Math.min(4, heading.level - 1) * 20
                                        ) + "px",
                                    // marginRight: "-4px",
                                }}
                                onClick={() => {
                                    if (!editor) return;
                                    const node = editor?.view.nodeDOM(
                                        heading.pos
                                    );
                                    const element = node as HTMLElement;
                                    element.scrollIntoView({
                                        behavior: "smooth",
                                        block: "start",
                                    });
                                }}
                            >
                                {heading.text}
                            </TableOfContentsBTN>
                        );
                    })}
                </div>
            </div>
        </TableOfContentsContainer>
    );
}

export default TableOfContents;

function TableOfContentsContainer(props: { children: ReactNode }) {
    const { theme } = useTheme();
    const [scrollOffset, setScrollOffset] = useState(0);
    const editor = useGetEditor().editor.current;

    useEffect(() => {
        const editorEl = document.querySelector("#scroll-content");
        if (!editorEl) return;

        const observer = new ResizeObserver(() => {
            const canScroll = editorEl.scrollHeight > editorEl.clientHeight;
            setScrollOffset(canScroll ? 8 : 0);
        });

        observer.observe(editorEl);

        return () => {
            observer.disconnect();
        };
    }, [editor]);

    return (
        <div
            className="absolute p-2 pb-3 px-3 rounded-md overflow-hidden"
            style={{
                top: "0px",
                right: scrollOffset + 5 + "px",
                minWidth: "200px",
                maxWidth: "350px",
                backgroundColor: hexToRGBA(theme.secondaryHeaderColor, 0.93),
                backdropFilter: "blur(5px)",
                border: "1px solid " + theme.headerColor,
            }}
        >
            {props.children}
        </div>
    );
}
